import React, { useState, useEffect } from 'react';
import AppointmentCalendar from '../../components/appointment-calendar/AppointmentCalendar';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useTheme } from '../../context/ThemeContext';
import {
  Plus,
  Clock,
  User,
  Phone,
  Check,
  X,
  Loader2,
  UserCheck,
  UserX,
  Star,
  Trash2,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Appointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewForm, setShowNewForm] = useState(false);
  const [createError, setCreateError] = useState('');
  const [reactivation, setReactivation] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [newAppointment, setNewAppointment] = useState({
    patient_name: '',
    patient_phone: '',
    service_name: '',
    appointment_date: '',
    duration_minutes: 30
  });
  const { theme } = useTheme();

  const cardBg = theme === 'dark' ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm';
  const inputBg = theme === 'dark' ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900';
  const selectBg = theme === 'dark' ? 'bg-slate-700/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900';
  const textPrimary = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const textSecondary = theme === 'dark' ? 'text-gray-400' : 'text-gray-600';
  const textMuted = theme === 'dark' ? 'text-gray-500' : 'text-gray-500';
  const dividerBg = theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    let clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');

    if (!clinicId) {
      clinicId = '0476280a-82ca-4713-bb61-0a9ade950bcd';
      localStorage.setItem('clinicId', clinicId);
    }

    setLoadError('');
    try {
      const [appointmentsRes, clinicRes] = await Promise.all([
        axios.get(`${API}/clinic/${clinicId}/appointments`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }),
        axios.get(`${API}/clinic/${clinicId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
      ]);
      setAppointments(appointmentsRes.data || []);
      setClinic(clinicRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoadError('No se pudo cargar la agenda. Reintenta para consultar las citas.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    const appointmentDate = new FormData(e.currentTarget).get('appointment_date');
    setCreateError('');
    let clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    if (!clinicId) clinicId = '0476280a-82ca-4713-bb61-0a9ade950bcd';

    try {
      await axios.post(`${API}/clinic/${clinicId}/appointments`, null, {
        params: { ...newAppointment, appointment_date: appointmentDate },
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setShowNewForm(false);
      setNewAppointment({
        patient_name: '',
        patient_phone: '',
        service_name: '',
        appointment_date: '',
        duration_minutes: 30
      });
      loadData();
    } catch (error) {
      console.error('Error creating appointment:', error);
      setCreateError('No se pudo guardar la cita. Revisa los datos e intenta nuevamente.');
    }
  };

  const reactivateAppointment = async (appointmentId, patientName) => {
    setReactivation({ id: appointmentId, patientName });
  };

  const updateStatus = async (appointmentId, status) => {
    let clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    if (!clinicId) clinicId = '0476280a-82ca-4713-bb61-0a9ade950bcd';
    try {
      await axios.patch(`${API}/clinic/${clinicId}/appointments/${appointmentId}`, { status }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (status === 'completed') {
        alert('✅ Cita marcada como completada. Se enviará solicitud de reseña.');
      } else if (status === 'no_show') {
        alert('❌ Cita marcada como no asistió.');
      } else if (status === 'pending') {
        alert('✅ Cita reactivada como pendiente.');
      } else if (status === 'confirmed') {
        alert('✅ Cita marcada como confirmada.');
      } else if (status === 'cancelled') {
        alert('Cita cancelada.');
      }

      loadData();
    } catch (error) {
      console.error('Error updating appointment:', error);
      alert('Error al actualizar la cita');
    }
  };

  const deleteAppointment = async (appointmentId, patientName) => {
    if (!window.confirm(`¿Eliminar la cita de ${patientName}? También se eliminará de Google Calendar.`)) {
      return;
    }

    let clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    if (!clinicId) clinicId = '0476280a-82ca-4713-bb61-0a9ade950bcd';

    try {
      await axios.delete(`${API}/clinic/${clinicId}/appointments/${appointmentId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      alert('✅ Cita eliminada correctamente');
      loadData();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      alert('Error al eliminar la cita');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-500',
      confirmed: 'bg-green-500/20 text-green-500',
      cancelled: 'bg-red-500/20 text-red-500',
      completed: 'bg-blue-500/20 text-blue-500',
      no_show: 'bg-gray-500/20 text-gray-500'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-500';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendiente',
      confirmed: 'Confirmada',
      cancelled: 'Cancelada',
      completed: 'Completada',
      no_show: 'No asistió'
    };
    return labels[status] || status;
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit', timeZone: clinic?.timezone || 'America/Mexico_City'
    });
  };

  const AppointmentCard = ({ apt }) => (
    <Card key={apt.id} className={cardBg}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 text-center">
              <p className={`${textPrimary} font-bold`}>{formatTime(apt.appointment_date)}</p>
              <p className={`${textMuted} text-xs`}>{apt.duration_minutes} min</p>
            </div>
            <div className={`w-px h-12 ${dividerBg}`} />
            <div>
              <p className={`${textPrimary} font-medium flex items-center gap-2`}>
                <User className={`w-4 h-4 ${textMuted}`} />
                {apt.patient_name}
              </p>
              <p className={`${textMuted} text-sm flex items-center gap-2`}>
                <Phone className="w-3 h-3" />
                {apt.patient_phone}
              </p>
              <p className={`${textSecondary} text-sm mt-1`}>{apt.service_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-xs ${getStatusColor(apt.status)}`}>
              {getStatusLabel(apt.status)}
            </span>
            {apt.status === 'pending' && (
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => updateStatus(apt.id, 'confirmed')}
                  className="text-green-500 hover:text-green-400 hover:bg-green-500/10" title="Confirmar cita">
                  <Check className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => updateStatus(apt.id, 'cancelled')}
                  className="text-red-500 hover:text-red-400 hover:bg-red-500/10" title="Cancelar cita">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
            {apt.status === 'confirmed' && (
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => updateStatus(apt.id, 'completed')}
                  className="text-green-500 hover:text-green-400 hover:bg-green-500/10 flex items-center gap-1"
                  data-testid={`btn-attended-${apt.id}`}>
                  <UserCheck className="w-4 h-4" /><span className="text-xs">Asistió</span>
                </Button>
                <Button size="sm" variant="ghost" onClick={() => updateStatus(apt.id, 'no_show')}
                  className="text-red-500 hover:text-red-400 hover:bg-red-500/10 flex items-center gap-1"
                  data-testid={`btn-noshow-${apt.id}`}>
                  <UserX className="w-4 h-4" /><span className="text-xs">No asistió</span>
                </Button>
              </div>
            )}
            {apt.status === 'completed' && apt.review_request_sent && (
              <span className="text-yellow-500 flex items-center gap-1 text-xs">
                <Star className="w-3 h-3" /> Reseña enviada
              </span>
            )}
            {apt.status === 'no_show' && (
              reactivation?.id === apt.id ? (
                <div className="flex items-center gap-2" role="group" aria-label="Confirmar reactivación">
                  <span className={`${textSecondary} text-xs`}>¿Reactivar la cita de {reactivation.patientName}?</span>
                  <Button size="sm" onClick={async () => {
                    setReactivation(null);
                    await updateStatus(apt.id, 'pending');
                  }}>Sí, reactivar</Button>
                  <Button size="sm" variant="ghost" onClick={() => setReactivation(null)}>Cancelar</Button>
                </div>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => reactivateAppointment(apt.id, apt.patient_name)}
                  className="text-green-500 hover:text-green-400 hover:bg-green-500/10 flex items-center gap-1"
                  title="Reactivar cita"
                  data-testid={`btn-reactivate-${apt.id}`}>
                  <Clock className="w-4 h-4" /><span className="text-xs">Reactivar</span>
                </Button>
              )
            )}
            <Button size="sm" variant="ghost" onClick={() => deleteAppointment(apt.id, apt.patient_name)}
              className="text-gray-400 hover:text-red-500 hover:bg-red-500/10"
              data-testid={`btn-delete-${apt.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <DashboardLayout title="Citas">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Citas">
      <div className="flex justify-end mb-3"><Button onClick={() => setShowNewForm(true)}><Plus className="w-4 h-4 mr-2"/>Nueva cita</Button></div>
      {/* New Appointment Form */}
      {showNewForm && (
        <Card className={`${cardBg} mb-6`}>
          <CardHeader>
            <CardTitle className={textPrimary}>Nueva Cita</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateAppointment} className="space-y-4">
              {createError && <p role="alert" className="text-red-500">{createError}</p>}
              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  placeholder="Nombre del paciente"
                  value={newAppointment.patient_name}
                  onChange={(e) => setNewAppointment({ ...newAppointment, patient_name: e.target.value })}
                  required
                  className={inputBg}
                />
                <Input
                  placeholder="Teléfono"
                  value={newAppointment.patient_phone}
                  onChange={(e) => setNewAppointment({ ...newAppointment, patient_phone: e.target.value })}
                  required
                  className={inputBg}
                />
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <select
                  value={newAppointment.service_name}
                  onChange={(e) => setNewAppointment({ ...newAppointment, service_name: e.target.value })}
                  required
                  className={`${selectBg} border rounded-md px-3 py-2`}
                >
                  <option value="">Seleccionar servicio</option>
                  {clinic?.services?.map((service) => (
                    <option key={service.id} value={service.name}>{service.name}</option>
                  ))}
                  <option value="Consulta general">Consulta general</option>
                </select>
                <Input
                  type="datetime-local"
                  name="appointment_date"
                  value={newAppointment.appointment_date}
                  onChange={(e) => setNewAppointment({ ...newAppointment, appointment_date: e.target.value })}
                  required
                  className={inputBg}
                />
                <select
                  value={newAppointment.duration_minutes}
                  onChange={(e) => setNewAppointment({ ...newAppointment, duration_minutes: parseInt(e.target.value) })}
                  className={`${selectBg} border rounded-md px-3 py-2`}
                >
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={45}>45 min</option>
                  <option value={60}>1 hora</option>
                </select>
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">
                  Crear Cita
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowNewForm(false)} className={textSecondary}>
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <AppointmentCalendar appointments={appointments} clinic={clinic} error={loadError} onRefresh={loadData}
        renderAppointment={apt => <AppointmentCard apt={apt}/>}
        onCreate={date => {setNewAppointment(value => ({...value, appointment_date: date}));setCreateError('');setShowNewForm(true);window.scrollTo({top:0,behavior:'smooth'});}}/>
    </DashboardLayout>
  );
};

export default Appointments;

